'use client';
import { useEffect, useRef, useState } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import {
  buildVidUrlMP4,
  buildVidUrlWebM,
  getPlaceholderImage,
} from '@repo/ui/src/functions/video-functions';
import { MediaType } from '@repo/ui/src/types/image';
import VideoPauseSmall from '@repo/ui/src/components/Icons/VideoPauseSmall';
import VideoPlaySmall from '@repo/ui/src/components/Icons/VideoPlaySmall';
import noop from 'lodash/noop';

interface Props {
  video: MediaType;
  width: number;
  fillContainer?: boolean;
  className?: string;
  playButton?: boolean;
  videoOverlay?: boolean;
  playButtonPosition?: 'left' | 'right';
}

const BackgroundVideo = ({
  video,
  width,
  fillContainer = false,
  className = '',
  playButton = true,
  videoOverlay,
  playButtonPosition = 'left',
}: Props) => {
  let speed = '3g';
  let vidWidth;
  let vidHeight;
  if (width && width <= 854 && !fillContainer) {
    vidWidth = 854;
    vidHeight = 480;
  } else if (width && width <= 1280 && !fillContainer) {
    vidWidth = 1280;
    vidHeight = 720;
  } else if (width && width >= 1280 && !fillContainer) {
    vidWidth = 1920;
    vidHeight = 1080;
  } else {
    vidWidth = '100%';
    vidHeight = 'auto';
  }
  if (typeof window !== 'undefined') {
    const connection =
      // @ts-expect-error connection is not defined on navigator
      navigator?.connection;
    speed = connection?.effectiveType || speed;
  }
  if (speed !== '2g' && width < 427) {
    speed = '3g';
  } else if (speed === '4g' && width < 854) {
    speed = '3g';
  }
  const thumbnail = getPlaceholderImage(video, vidWidth);
  const vidUrlMp4 = buildVidUrlMP4(video, vidWidth, speed);
  const vidUrlWebM = buildVidUrlWebM(video, vidWidth, speed);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(true);
  const toggle = () => setPlaying((curr) => !curr);

  useEffect(() => {
    if (playing) {
      videoRef.current?.play()?.catch(noop);
    } else {
      videoRef.current?.pause();
    }
  }, [playing]);

  return (
    <div className={classNames('tw-w-full tw-bg-neutral-600', className)}>
      <div
        data-testid="video-container"
        className={classNames(
          'tw-flex tw-content-center tw-items-center tw-w-full tw-overflow-hidden tw-relative tw-h-full',
          fillContainer && 'tw-max-h-full',
          !fillContainer && (Number(vidHeight) > 720 ? 'tw-max-h-[720px]' : 'tw-max-h-[480px]'),
        )}
      >
        {playButton && (
          <button
            onClick={toggle}
            aria-label={playing ? 'pause video' : 'play video'}
            className={classNames(
              'tw-absolute tw-bottom-5 tw-z-[1010] hover:tw-text-brand-400',
              playButtonPosition === 'left' && 'tw-left-10 md:tw-left-5',
              playButtonPosition === 'right' && 'tw-right-10 md:tw-right-5',
            )}
          >
            {playing ? <VideoPauseSmall /> : <VideoPlaySmall />}
          </button>
        )}
        {videoOverlay && (
          <div
            data-testid="video-overlay"
            className="tw-absolute tw-top-0 tw-bottom-0 tw-right-0 tw-left-0 tw-w-full tw-h-full tw-bg-[rgba(0,0,0,0.55)]"
          />
        )}

        <video
          autoPlay
          loop
          muted
          poster={thumbnail}
          playsInline
          preload="auto"
          width={vidWidth}
          height={vidHeight}
          key={vidUrlMp4}
          ref={videoRef}
          data-testid="background-video"
          className={classNames(
            'tw-object-cover tw-h-full motion-reduce:tw-hidden',
            fillContainer && 'tw-min-h-[480px]',
          )}
        >
          <source src={vidUrlMp4} type="video/mp4" />
          <source src={vidUrlWebM} type="video/webm" />
        </video>
      </div>
    </div>
  );
};

export default BackgroundVideo;
